import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { useStore, addFromCatalog, openBuilder, closeSheet } from "../state/store";
import { CATALOG, defOf, isRound, type CatalogDef } from "../engine/catalog";
import { FLOORS } from "../engine/constants";
import { requestThumb } from "../engine/thumbs";
import { persp, ortho, walk } from "../scene/rig";

const cmv = (v: number) => Math.round(v * 100);

/** wohin ein neues Möbel kommt: im Rundgang vor die Nase, sonst in die Bildmitte des gewählten Geschosses */
export function placeFromView(c: CatalogDef) {
  const s = useStore.getState();
  if (s.mode === "walk") {
    addFromCatalog(c, { f: walk.f, x: walk.x - Math.sin(walk.yaw) * 1.6, y: walk.y + Math.cos(walk.yaw) * 1.6, walking: true });
    return;
  }
  const f = s.floor === "all" ? 1 : s.floor;
  const cam = s.mode === "plan" ? ortho : persp;
  const ray = new THREE.Raycaster(); ray.setFromCamera(new THREE.Vector2(0, 0.1), cam);
  const p = new THREE.Vector3();
  const hit = ray.ray.intersectPlane(new THREE.Plane(new THREE.Vector3(0, 1, 0), -FLOORS[f].z), p);
  addFromCatalog(c, { f, x: hit ? p.x : 3.8, y: hit ? -p.z : 5.5, walking: false });
}

export function CatalogSheet() {
  const open = useStore((s) => s.sheet === "catalog");
  const custom = useStore((s) => s.custom);
  const [q, setQ] = useState("");
  const all = useMemo(() => {
    const defs = [...custom.map((c) => defOf(c.key, custom)), ...CATALOG];
    const ql = q.trim().toLowerCase();
    return defs.filter((c) => !ql || (c.name + " " + c.cat).toLowerCase().includes(ql));
  }, [custom, q]);
  const cats = useMemo(() => [...new Set(all.map((c) => c.cat))], [all]);
  if (!open) return null;
  return (
    <section className="sheet" id="catalog" aria-label="Möbel hinzufügen">
      <div className="sh-head"><h2>Möbel hinzufügen</h2><button className="x" onClick={closeSheet} aria-label="Schließen">×</button></div>
      <div className="sh-tools">
        <input id="cat-q" type="search" placeholder="Suchen, z. B. Spüle, Bett, Wallbox …" aria-label="Möbel suchen" value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btn small primary" id="cat-build" onClick={() => openBuilder(null)}>Eigenes Möbel bauen</button>
      </div>
      <div className="sh-body" id="catbody">
        {!all.length && <p className="note">Nichts gefunden. Mit „Eigenes Möbel bauen“ kannst du es selbst anlegen.</p>}
        {cats.map((cat) => (
          <div className="cat" key={cat}>
            <h3>{cat}</h3>
            <div className="grid">
              {all.filter((c) => c.cat === cat).map((c) => <CatalogItem key={c.key} def={c} q={q} />)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CatalogItem({ def, q }: { def: CatalogDef; q: string }) {
  const img = useRef<HTMLImageElement>(null);
  // nach jeder Suche neu anfordern: sichtbare Treffer rücken in der Warteschlange nach vorn
  useEffect(() => { requestThumb(def, (src) => { if (img.current) img.current.src = src; }); }, [def, q]);
  return (
    <div className="item-wrap">
      <button className="item" onClick={() => placeFromView(def)}>
        <img ref={img} alt="" width={48} height={48} />
        <div>
          <span>{def.name}</span>
          <small>{isRound(def.id) ? `Ø ${cmv(def.w)} cm` : `${cmv(def.w)} × ${cmv(def.d)} × ${cmv(def.h)} cm`}</small>
        </div>
      </button>
      {def.id === "custom" && <button className="edit" onClick={(e) => { e.stopPropagation(); openBuilder(def.key); }}>Bearbeiten</button>}
    </div>
  );
}
